import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  GraduationCap, CheckCircle2, XCircle, Award, Clock,
  Plus, Trash2, ArrowRight, RotateCcw, Trophy, Target
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuizCard({ quiz, onStart, onDelete, isAdmin, bestScore }) {
  const catColors = {
    'Food Safety': 'from-red-500 to-orange-500',
    'Diamond 1': 'from-blue-500 to-cyan-500',
    'Diamond 2': 'from-purple-500 to-violet-500',
  };
  const gradient = catColors[quiz.category] || 'from-slate-500 to-slate-600';

  return (
    <Card className="border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`quiz-card-${quiz.id}`}>
      <div className={`bg-gradient-to-r ${gradient} p-4`}>
        <div className="flex items-start justify-between">
          <GraduationCap className="w-6 h-6 text-white" />
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(quiz.id); }} className="text-white/70 hover:text-white hover:bg-white/20 h-7 px-2">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        <h3 className="text-lg font-bold text-white mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{quiz.title}</h3>
        <p className="text-white/70 text-xs mt-1">{quiz.category} &middot; {quiz.questions?.length || 0} questions</p>
      </div>
      <CardContent className="p-4">
        <p className="text-sm text-slate-600 mb-3 line-clamp-2">{quiz.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Target className="w-3 h-3" />
            Pass: {quiz.passing_score}%
          </div>
          {bestScore !== null && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${bestScore >= quiz.passing_score ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
              Best: {bestScore}%
            </span>
          )}
        </div>
        <Button onClick={() => onStart(quiz)} className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`start-quiz-${quiz.id}`}>
          {bestScore !== null ? <><RotateCcw className="w-4 h-4 mr-2" /> Retake</> : <><ArrowRight className="w-4 h-4 mr-2" /> Start Quiz</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function QuizTaker({ quiz, onComplete, onCancel }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(Array(quiz.questions.length).fill(-1));
  const [submitting, setSubmitting] = useState(false);

  const q = quiz.questions[currentQ];

  const selectAnswer = (idx) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = idx;
    setAnswers(newAnswers);
  };

  const submit = async () => {
    if (answers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/training/quizzes/${quiz.id}/submit`, answers);
      onComplete(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="quiz-taker">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{quiz.title}</h2>
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>

      {/* Progress */}
      <div className="flex gap-1">
        {quiz.questions.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i === currentQ ? 'bg-blue-500' : answers[i] >= 0 ? 'bg-green-400' : 'bg-slate-200'}`} />
        ))}
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-6">
          <p className="text-xs text-slate-500 mb-2">Question {currentQ + 1} of {quiz.questions.length}</p>
          <h3 className="text-lg font-semibold text-slate-900 mb-5">{q.question}</h3>
          <div className="space-y-3">
            {q.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => selectAnswer(i)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${answers[currentQ] === i ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}
                data-testid={`quiz-option-${i}`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold ${answers[currentQ] === i ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {String.fromCharCode(65 + i)}
                </span>
                {opt}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>Previous</Button>
        {currentQ < quiz.questions.length - 1 ? (
          <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={answers[currentQ] < 0}>
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting || answers.includes(-1)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-quiz-btn">
            {submitting ? 'Submitting...' : 'Submit Quiz'}
          </Button>
        )}
      </div>
    </div>
  );
}

function QuizResults({ result, quiz, onRetake, onBack }) {
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="quiz-results">
      <Card className={`border-2 ${result.passed ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'}`}>
        <CardContent className="p-8 text-center">
          {result.passed ? (
            <Trophy className="w-16 h-16 mx-auto text-green-500 mb-4" />
          ) : (
            <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          )}
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {result.passed ? 'Congratulations!' : 'Keep Trying!'}
          </h2>
          <p className="text-4xl font-bold mb-2 text-slate-900">{result.score}%</p>
          <p className="text-slate-600">
            {result.correct}/{result.total} correct &middot; Passing: {result.passing_score}%
          </p>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-slate-900">Answer Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {result.results?.map((r, i) => (
            <div key={i} className={`p-3 rounded-lg ${r.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-2">
                {r.is_correct ? <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />}
                <div>
                  <p className="text-sm font-medium text-slate-900">{r.question}</p>
                  <p className="text-xs mt-1">Your answer: <strong>{r.your_answer}</strong></p>
                  {!r.is_correct && <p className="text-xs text-green-700">Correct: <strong>{r.correct_answer}</strong></p>}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onBack}>Back to Quizzes</Button>
        {!result.passed && <Button onClick={onRetake} className="bg-blue-600 hover:bg-blue-700 text-white"><RotateCcw className="w-4 h-4 mr-2" /> Retake Quiz</Button>}
      </div>
    </div>
  );
}

export default function TrainingQuizzes() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const isAdmin = ['owner', 'area_manager'].includes(user?.role);

  const [newQuiz, setNewQuiz] = useState({
    title: '', category: 'Diamond 1', description: '', passing_score: 70,
    questions: [{ question: '', options: ['', '', '', ''], correct_index: 0 }]
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [qRes, rRes] = await Promise.all([
        axios.get(`${API_URL}/training/quizzes`),
        axios.get(`${API_URL}/training/my-quiz-results`)
      ]);
      setQuizzes(qRes.data || []);
      setMyResults(rRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getBestScore = (quizId) => {
    const attempts = myResults.filter(r => r.quiz_id === quizId);
    if (attempts.length === 0) return null;
    return Math.max(...attempts.map(a => a.score));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/training/quizzes/${id}`);
      toast.success('Quiz deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleCreate = async () => {
    if (!newQuiz.title || newQuiz.questions.some(q => !q.question || q.options.some(o => !o))) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/training/quizzes`, newQuiz);
      toast.success('Quiz created');
      setCreateOpen(false);
      setNewQuiz({ title: '', category: 'Diamond 1', description: '', passing_score: 70, questions: [{ question: '', options: ['', '', '', ''], correct_index: 0 }] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const addQuestion = () => {
    setNewQuiz({ ...newQuiz, questions: [...newQuiz.questions, { question: '', options: ['', '', '', ''], correct_index: 0 }] });
  };

  const updateQuestion = (qi, field, value) => {
    const qs = [...newQuiz.questions];
    qs[qi] = { ...qs[qi], [field]: value };
    setNewQuiz({ ...newQuiz, questions: qs });
  };

  const updateOption = (qi, oi, value) => {
    const qs = [...newQuiz.questions];
    qs[qi] = { ...qs[qi], options: qs[qi].options.map((o, i) => i === oi ? value : o) };
    setNewQuiz({ ...newQuiz, questions: qs });
  };

  const removeQuestion = (qi) => {
    if (newQuiz.questions.length <= 1) return;
    setNewQuiz({ ...newQuiz, questions: newQuiz.questions.filter((_, i) => i !== qi) });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Loading quizzes...</div>;
  }

  // Active quiz mode
  if (activeQuiz && !quizResult) {
    return <QuizTaker quiz={activeQuiz} onComplete={(r) => setQuizResult(r)} onCancel={() => setActiveQuiz(null)} />;
  }

  // Results mode
  if (quizResult) {
    return (
      <QuizResults
        result={quizResult}
        quiz={activeQuiz}
        onRetake={() => setQuizResult(null)}
        onBack={() => { setQuizResult(null); setActiveQuiz(null); fetchData(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="training-quizzes-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <GraduationCap className="w-8 h-8" />
              Training Quizzes
            </h1>
            <p className="text-indigo-100">Test your knowledge and earn certifications</p>
          </div>
          {isAdmin && (
            <Button onClick={() => setCreateOpen(true)} className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold" data-testid="create-quiz-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Quiz
            </Button>
          )}
        </div>
      </div>

      {/* My Stats */}
      {myResults.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{myResults.length}</p>
              <p className="text-xs text-blue-600">Attempts</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{myResults.filter(r => r.passed).length}</p>
              <p className="text-xs text-green-600">Passed</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{myResults.length > 0 ? Math.round(myResults.reduce((s, r) => s + r.score, 0) / myResults.length) : 0}%</p>
              <p className="text-xs text-amber-600">Avg Score</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quiz Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quizzes.map(q => (
          <QuizCard
            key={q.id}
            quiz={q}
            onStart={(quiz) => setActiveQuiz(quiz)}
            onDelete={handleDelete}
            isAdmin={isAdmin}
            bestScore={getBestScore(q.id)}
          />
        ))}
      </div>

      {quizzes.length === 0 && (
        <Card className="bg-white border border-slate-200">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Quizzes Available</h3>
            <p className="text-slate-500">Quizzes will appear here once created by admin</p>
          </CardContent>
        </Card>
      )}

      {/* Create Quiz Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Training Quiz</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={newQuiz.title} onChange={(e) => setNewQuiz({ ...newQuiz, title: e.target.value })} placeholder="Quiz title" />
              </div>
              <div>
                <Label>Category</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={newQuiz.category} onChange={(e) => setNewQuiz({ ...newQuiz, category: e.target.value })}>
                  <option>Diamond 1</option>
                  <option>Diamond 2</option>
                  <option>Food Safety</option>
                  <option>Operations</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newQuiz.description} onChange={(e) => setNewQuiz({ ...newQuiz, description: e.target.value })} placeholder="Brief description" />
            </div>
            <div>
              <Label>Passing Score (%)</Label>
              <Input type="number" value={newQuiz.passing_score} onChange={(e) => setNewQuiz({ ...newQuiz, passing_score: parseInt(e.target.value) || 70 })} />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Questions</Label>
                <Button variant="outline" size="sm" onClick={addQuestion}><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              {newQuiz.questions.map((q, qi) => (
                <div key={qi} className="bg-slate-50 rounded-lg p-4 mb-3 border border-slate-200">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-500">Q{qi + 1}</span>
                    {newQuiz.questions.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeQuestion(qi)} className="h-6 px-2 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                  <Input className="mb-2" value={q.question} onChange={(e) => updateQuestion(qi, 'question', e.target.value)} placeholder="Question text" />
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 mb-1">
                      <input type="radio" name={`correct-${qi}`} checked={q.correct_index === oi} onChange={() => updateQuestion(qi, 'correct_index', oi)} />
                      <Input className="flex-1" value={opt} onChange={(e) => updateOption(qi, oi, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oi)}`} />
                    </div>
                  ))}
                  <p className="text-[10px] text-slate-400 mt-1">Select the radio button next to the correct answer</p>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white">Create Quiz</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
